import React, { useLayoutEffect, useRef } from 'react';
import '../Onboarding.css';
import './GuidedTourOverlay.css';

export interface GuidedTourOverlayProps {
  isOpen: boolean;
  tourTitle: string;
  stepsCount: number;
  currentStep: number;
  stepTitle: string;
  stepDescription: string;
  contextLabel?: string;
  tooltipX: number;
  tooltipY: number;
  position?: string;
  dataStep?: string;
  isStepTransitioning?: boolean;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
  showStepDots?: boolean;
}

const GuidedTourOverlay: React.FC<GuidedTourOverlayProps> = ({
  isOpen,
  tourTitle,
  stepsCount,
  currentStep,
  stepTitle,
  stepDescription,
  contextLabel,
  tooltipX,
  tooltipY,
  position = 'top',
  dataStep,
  isStepTransitioning = false,
  onClose,
  onNext,
  onPrev,
  showStepDots = true,
}) => {
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    document.documentElement.style.setProperty('--tooltip-x', `${tooltipX}px`);
    document.documentElement.style.setProperty('--tooltip-y', `${tooltipY}px`);
  }, [tooltipX, tooltipY]);

  if (!isOpen || stepsCount === 0) return null;

  const safeIndex = Math.min(Math.max(0, currentStep), stepsCount - 1);
  const progress = stepsCount > 1 ? ((safeIndex + 1) / stepsCount) * 100 : 100;
  const isLast = safeIndex >= stepsCount - 1;

  return (
    <div className="onboarding-overlay guided-tour-overlay" role="presentation">
      <div className="onboarding-backdrop guided-tour-backdrop" aria-hidden="true" />

      <div
        ref={tooltipRef}
        className={`onboarding-tooltip guided-tour-tooltip onboarding-tooltip--tour${
          isStepTransitioning ? ' guided-tour-tooltip--transitioning' : ''
        }`}
        data-position={position}
        data-step={dataStep ?? ''}
        role="dialog"
        aria-modal="true"
        aria-labelledby="guided-tour-step-title"
        aria-describedby="guided-tour-step-description"
        style={{
          transform: 'translate(-50%, -50%)',
        }}
      >
        <div className="guided-tour-tooltip__header">
          <div className="guided-tour-tooltip__meta">
            <span className="guided-tour-tooltip__eyebrow">
              <i className="fas fa-compass" aria-hidden="true" />
              {tourTitle}
            </span>
            <span className="guided-tour-tooltip__counter">
              Step {safeIndex + 1} of {stepsCount}
            </span>
          </div>
          <div
            className="guided-tour-tooltip__progress"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress)}
            aria-label={`Tour progress, step ${safeIndex + 1} of ${stepsCount}`}
          >
            <span className="guided-tour-tooltip__progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <div key={safeIndex} className="tooltip-content guided-tour-tooltip__body guided-tour-tooltip__body--step">
          <h3 id="guided-tour-step-title">
            <span className="step-number">{safeIndex + 1}</span>
            {stepTitle}
          </h3>
          {contextLabel ? (
            <p className="guided-tour-context-label">{contextLabel}</p>
          ) : null}
          <p id="guided-tour-step-description" className="guided-tour-description">
            {stepDescription}
          </p>

          <div className="tooltip-actions guided-tour-tooltip__actions">
            <button type="button" className="skip-btn guided-tour-btn guided-tour-btn--ghost" onClick={onClose}>
              Skip tour
            </button>
            {showStepDots ? (
              <div className="step-indicators guided-tour-dots" aria-hidden="true">
                {Array.from({ length: stepsCount }, (_, index) => (
                  <div
                    key={index}
                    className={`step-dot ${index === safeIndex ? 'active' : ''} ${index < safeIndex ? 'completed' : ''}`}
                  />
                ))}
              </div>
            ) : (
              <span className="guided-tour-dots-placeholder" aria-hidden="true" />
            )}
            <div className="navigation-buttons guided-tour-nav">
              {safeIndex > 0 ? (
                <button type="button" className="prev-btn guided-tour-btn guided-tour-btn--ghost" onClick={onPrev}>
                  Previous
                </button>
              ) : null}
              <button type="button" className="next-btn guided-tour-btn guided-tour-btn--primary" onClick={onNext}>
                {isLast ? 'Finish' : 'Next'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuidedTourOverlay;
